import { Calendar } from './calendar';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DayComponentPropsType,
  EmptyDayComponentPropsType,
  GroupedJobsType,
  HoveredDateType,
  CalendarPagePropsType,
} from './types';
import { ModalMode, JobsType } from '../types';
import { Job } from 'common/state/jobs/actions';
import { doGetJobs } from 'common/state/jobs/thunkActions';
import {
  ExpandJobContainer,
  HiddenJobContainer,
  JobContainer,
  NoJobsMessage,
} from './elements';
import { REFETCH_PERIOD, RUNNING_JOB_STATUSES } from '../config';
import { StatusIcon } from '../statusIcon';
import Icon from '@mdi/react';
import { mdiPlus } from '@mdi/js';

const EmptyDayComponent = ({
  dateString,
  hoveredDate,
  hoveredJobId,
}: EmptyDayComponentPropsType) => (
  <>
    <HiddenJobContainer
      visible={hoveredDate?.dateString === dateString && !hoveredJobId}
    >
      <Icon path={mdiPlus} size={0.5} />
      <span>Create Job</span>
    </HiddenJobContainer>
    <NoJobsMessage>No Jobs</NoJobsMessage>
  </>
);

const DayJobItem = ({ job, setFocusedJob, setModalMode, setHoveredJobId }) => {
  return (
    <JobContainer
      key={job.id}
      type={job.type}
      onClick={() => {
        setFocusedJob(job.id);
        setModalMode('job');
      }}
      onMouseEnter={() => setHoveredJobId(job.id)}
      onMouseLeave={() => setHoveredJobId(null)}
    >
      <StatusIcon status={job.status} size={0.5} />
      <span>{job.type.replaceAll('_', ' ')}</span>
    </JobContainer>
  );
};

const DayComponentGenerator =
  (
    jobs: JobsType,
    groupedJobIds: GroupedJobsType,
    hoveredJobId: number,
    weekCount: number,
    expandedDates: Set<string>,
    setFocusedJob: (id: number) => void,
    setModalMode: (mode: ModalMode) => void,
    setHoveredJobId: (id: number) => void,
    setExpandedDates: (dates: Set<string>) => void
  ) =>
  ({ dateString, hoveredDate }: DayComponentPropsType) => {
    if (!groupedJobIds[dateString])
      return (
        <EmptyDayComponent
          dateString={dateString}
          hoveredDate={hoveredDate}
          hoveredJobId={hoveredJobId}
        />
      );

    const defaultDisplayCount = weekCount === 5 ? 4 : weekCount === 2 ? 6 : 8;
    const dayJobIds = groupedJobIds[dateString];
    const displayJobIds = dayJobIds.slice(
      0,
      dayJobIds.length > defaultDisplayCount && !expandedDates.has(dateString)
        ? defaultDisplayCount - 1
        : dayJobIds.length
    );

    return (
      <>
        {displayJobIds.map((jobId) => (
          <DayJobItem
            key={jobId}
            job={jobs[jobId]}
            setFocusedJob={setFocusedJob}
            setModalMode={setModalMode}
            setHoveredJobId={setHoveredJobId}
          />
        ))}
        {dayJobIds.length > displayJobIds.length && (
          <ExpandJobContainer
            onMouseEnter={() => setHoveredJobId(-1)}
            onMouseLeave={() => setHoveredJobId(null)}
            onClick={() =>
              setExpandedDates(new Set([dateString, ...expandedDates]))
            }
          >
            <div />
            <span>+ {dayJobIds.length - displayJobIds.length} more</span>
          </ExpandJobContainer>
        )}
        {displayJobIds.length > defaultDisplayCount && (
          <ExpandJobContainer
            onMouseEnter={() => setHoveredJobId(-1)}
            onMouseLeave={() => setHoveredJobId(null)}
            onClick={() => {
              const newExpandedDates = new Set([...expandedDates]);
              newExpandedDates.delete(dateString);
              setExpandedDates(newExpandedDates);
            }}
          >
            <div />
            <span>- Hide</span>
          </ExpandJobContainer>
        )}
        <HiddenJobContainer
          visible={hoveredDate?.dateString === dateString && !hoveredJobId}
        >
          <Icon path={mdiPlus} size={0.5} />
          <span>Create Job</span>
        </HiddenJobContainer>
      </>
    );
  };

export const CalendarPage = ({
  jobs,
  jobsDispatch,
  setModalMode,
  setFocusedJob,
  setCreateInitialData,
}: CalendarPagePropsType) => {
  const [expandedDates, setExpandedDates] = useState(new Set([]));
  const [weekCount, setWeekCount] = useState<number>();
  const [hoveredJobId, setHoveredJobId] = useState<number>();
  const refetchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const fetchJobs = useCallback(
    (dateRange: [from: Date, to: Date]) => {
      jobsDispatch(
        doGetJobs({
          date_param: [
            `gte:${dateRange[0].toISOString().slice(0, 10)}T00:00:00`,
            `lte:${dateRange[1].toISOString().slice(0, 10)}T23:59:59`,
          ],
        })
      );
    },
    [jobsDispatch]
  );

  const onDateClick = useCallback(
    (cell: HoveredDateType) => {
      if (!hoveredJobId) {
        setCreateInitialData({
          date_from: cell.dateString,
          date_to: cell.dateString,
        });
        setModalMode('create');
      }
    },
    [hoveredJobId, setCreateInitialData, setModalMode]
  );

  useEffect(() => {
    clearTimeout(refetchTimeout.current);
    const pendingIds = Object.values(jobs).reduce((acc: number[], job: Job) => {
      if (RUNNING_JOB_STATUSES.has(job.status)) acc.push(job.id);
      return acc;
    }, []);
    if (pendingIds.length) {
      refetchTimeout.current = setTimeout(
        () =>
          jobsDispatch(
            doGetJobs({
              id: pendingIds,
            })
          ),
        REFETCH_PERIOD
      );
    }
    return () => clearTimeout(refetchTimeout.current);
  }, [jobs, jobsDispatch]);

  const groupedJobIds = useMemo(
    (): GroupedJobsType =>
      Object.values(jobs)
        ?.sort((jobA: Job, jobB: Job) => {
          if (jobA.started > jobB.started) return 1;
          if (jobA.started < jobB.started) return -1;
          return 0;
        })
        ?.reduce((acc: GroupedJobsType, job: Job) => {
          const dateString = job.params.date.slice(0, 10);
          if (!acc[dateString]) acc[dateString] = [];
          acc[dateString].push(job.id);
          return acc;
        }, {}),
    // eslint-disable-next-line
    [jobs]
  );

  const DayComponent = useMemo(
    () =>
      DayComponentGenerator(
        jobs,
        groupedJobIds,
        hoveredJobId,
        weekCount,
        expandedDates,
        setFocusedJob,
        setModalMode,
        setHoveredJobId,
        setExpandedDates
      ),
    [
      jobs,
      groupedJobIds,
      hoveredJobId,
      weekCount,
      expandedDates,
      setFocusedJob,
      setModalMode,
      setHoveredJobId,
      setExpandedDates,
    ]
  );

  return (
    <>
      <Calendar
        DayComponent={DayComponent}
        onDateChange={(dateRange, weekCount) => {
          fetchJobs(dateRange);
          setWeekCount(weekCount);
        }}
        onClick={onDateClick}
      />
    </>
  );
};
